import { useEffect, useState, type ReactNode } from "react";
import { useAuth } from "../../auth/authContext.ts";
import {
  ApiError,
  CODIGO_BC_AUSENTE,
  apiConfigurada,
  cancelarFormatoExportacion,
  capturarAcuseFormatoExportacion,
  configurarAlertaFormatoExportacion,
  generarXmlExportacion,
  listarCasos,
  listarCatalogo,
  listarExportacionesFormato,
  listarInformes,
  listarPersonas,
  listarTemporizadores,
  obtenerXmlExportacion,
  presentarFormatoExportacion,
  subirAcuseFormatoExportacion,
  validarFormatoExportacion,
  type CasoRow,
  type FormatoExportacionRow,
  type InformeRow,
  type PersonaRow,
  type TemporizadorRow,
} from "../../lib/api.ts";
import { C, st, fecha } from "../../ui/theme.ts";
import { nombrePersona } from "../../ui/persona.ts";
import { IconoCumplimiento, IconoPersonas, IconoReloj, IconoReporte } from "../../ui/iconos.tsx";

/**
 * PENDIENTES — el "hoy" del REC: lo que requiere atención, agregado de lo que
 * el sistema ya vigila (casos abiertos, informes sin acuse, relojes de ley,
 * expedientes incompletos). No inventa señales: las junta.
 */

const TIPO_RELOJ: Record<string, string> = {
  informe_dia17: "Informe mensual — vence el día 17",
  aviso_dia17: "Aviso registrado — reloj de 17 días hábiles",
  efirma_por_vencer: "e.firma por vencer — renuévala en el SAT con tiempo",
  acumulacion_6m: "Ventana de acumulación de 6 meses",
  plazo_24h: "Plazo de 24 horas",
  requerimiento_10dh: "Requerimiento de la autoridad — 10 días hábiles",
  lineamientos_90d: "Lineamientos de identificación — reloj de 90 días",
  retencion_10a: "Retención de 10 años",
};

const ID_CORTO_LEN = 8;
const HASH_CORTO_LEN = 10;
const OPACIDAD_EN_PROCESO = 0.7;
const FOLIO_ACUSE_RE = /^[0-9]{4}-[1-9][0-9]{0,8}$/;
const ALERTA_SIN_ALERTA = "100";
const PRIORIDAD_NORMAL = 1;
const PRIORIDAD_24H = 2;
const ESTADO_INFORME_LABEL: Record<string, string> = {
  borrador: "Borrador",
  generado: "XML generado",
  firmado: "Paquete validado",
  presentado: "Presentado",
  acuse_capturado: "Acuse capturado",
  cancelado: "Cancelado",
};

type CatalogoOption = [string, string];

function opcionesCatalogo(rows: { clave: string; nombre: string }[]): CatalogoOption[] {
  return rows.map((r) => [r.clave, r.nombre]);
}

function botonAccion(tipo: "normal" | "primario" | "peligro" = "normal"): React.CSSProperties {
  return {
    padding: "8px 11px",
    borderRadius: 10,
    border: tipo === "primario" ? "none" : `1px solid ${tipo === "peligro" ? "#F2C4BE" : C.line}`,
    background: tipo === "primario" ? C.tiel : "#fff",
    color: tipo === "primario" ? "#fff" : tipo === "peligro" ? C.red : C.ink,
    fontSize: 12.5,
    fontWeight: 700,
    cursor: "pointer",
  };
}

function estadoInformeTexto(estado: string) {
  return ESTADO_INFORME_LABEL[estado] ?? estado.replace(/_/g, " ");
}

function tituloReloj(r: TemporizadorRow): string {
  if (r.tipo === "plazo_24h" && typeof r.metadatos["formato_exportacion_id"] === "string") {
    const tipo = typeof r.metadatos["tipo_alerta"] === "string" ? ` · alerta ${r.metadatos["tipo_alerta"]}` : "";
    return `Aviso INM con plazo de 24 horas${tipo}`;
  }
  if (r.tipo === "aviso_dia17" && typeof r.metadatos["formato_exportacion_id"] === "string") {
    const formato = typeof r.metadatos["formato_aviso"] === "string" ? ` (${r.metadatos["formato_aviso"]})` : "";
    return `Aviso con XML por generar${formato}`;
  }
  if (r.tipo === "aviso_dia17" && r.metadatos["adaptador_pendiente"] === true) {
    const formato = typeof r.metadatos["formato_oficial"] === "string" ? ` (${r.metadatos["formato_oficial"]})` : "";
    return `Aviso pendiente de adaptador Argus${formato}`;
  }
  if (r.tipo === "aviso_dia17" && r.metadatos["formato_pendiente"] === true) {
    return "Aviso pendiente de formato oficial";
  }
  return TIPO_RELOJ[r.tipo] ?? r.tipo;
}

export function PendientesView({ irA }: { irA: (s: string) => void }) {
  const { session } = useAuth();
  const token = session?.accessToken ?? "";
  const tenantId = session?.tenantId ?? "";
  const demo = !apiConfigurada() || token === "";

  const [relojes, setRelojes] = useState<TemporizadorRow[]>([]);
  const [casos, setCasos] = useState<CasoRow[]>([]);
  const [exportaciones, setExportaciones] = useState<FormatoExportacionRow[]>([]);
  const [informes, setInformes] = useState<InformeRow[]>([]);
  const [personas, setPersonas] = useState<PersonaRow[]>([]);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [foliosAcuse, setFoliosAcuse] = useState<Record<string, string>>({});
  const [archivosAcuse, setArchivosAcuse] = useState<Record<string, File | null>>({});
  const [alertasInm, setAlertasInm] = useState<CatalogoOption[]>([["100", "Sin alerta."], ["9999", "Otra alerta."]]);
  const [alertasAri, setAlertasAri] = useState<CatalogoOption[]>([["100", "Sin alerta."], ["9999", "Otra alerta."]]);
  const [alertaDrafts, setAlertaDrafts] = useState<Record<string, { prioridad: string; tipo_alerta: string; descripcion_alerta: string }>>({});
  const [error, setError] = useState<string | null>(null);
  const [avisoOk, setAvisoOk] = useState<string | null>(null);
  // Compuerta BC (issue BC-3): exportación cuyo aviso saldría sin beneficiario
  // controlador y espera confirmación explícita del REC antes de generarse.
  const [confirmBc, setConfirmBc] = useState<{ id: string; mensaje: string } | null>(null);
  const [cargando, setCargando] = useState(!demo);

  useEffect(() => {
    if (demo) return;
    setCargando(true);
    Promise.all([
      listarTemporizadores(token),
      listarCasos(token),
      listarInformes(token),
      listarPersonas(token),
      listarExportacionesFormato(token),
      listarCatalogo(token, "cat_inm_tipo_alerta"),
      listarCatalogo(token, "cat_ari_tipo_alerta"),
    ])
      .then(([t, c, i, p, x, alertasInmCat, alertasAriCat]) => {
        setRelojes(t);
        setCasos(c);
        setInformes(i);
        setPersonas(p);
        setExportaciones(x);
        setAlertasInm(opcionesCatalogo(alertasInmCat));
        setAlertasAri(opcionesCatalogo(alertasAriCat));
      })
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : "No se pudieron cargar los pendientes."); })
      .finally(() => { setCargando(false); });
  }, [demo, token]);

  const casosAbiertos = casos.filter((c) => c.estado === "abierto");
  const exportacionesPendientes = exportaciones.filter((x) => x.estado !== "acuse_capturado" && x.estado !== "cancelado");
  const informesPendientes = informes.filter((i) => i.estado !== "acuse_capturado" && i.estado !== "cancelado");
  const incompletas = personas.filter((p) => !p.expediente_completo);
  const hoy = new Date().toISOString().slice(0, "YYYY-MM-DD".length);

  const recargarExportaciones = async () => {
    if (demo) return;
    setExportaciones(await listarExportacionesFormato(token));
  };

  const alertaDraftDe = (x: FormatoExportacionRow) =>
    alertaDrafts[x.id] ?? {
      prioridad: String(x.prioridad),
      tipo_alerta: x.tipo_alerta,
      descripcion_alerta: x.descripcion_alerta ?? "",
    };

  const guardarAlertaExportacion = async (x: FormatoExportacionRow) => {
    const draft = alertaDraftDe(x);
    const prioridad = Number(draft.prioridad);
    const tipoAlerta = draft.tipo_alerta;
    const descripcion = draft.descripcion_alerta.trim();

    setError(null);
    setAvisoOk(null);
    setOcupado(x.id);
    try {
      if (prioridad === PRIORIDAD_NORMAL && tipoAlerta !== ALERTA_SIN_ALERTA) {
        throw new Error("La prioridad normal debe quedar sin alerta.");
      }
      if (prioridad === PRIORIDAD_24H && tipoAlerta === ALERTA_SIN_ALERTA) {
        throw new Error("La prioridad 24 horas requiere seleccionar una alerta.");
      }
      if (tipoAlerta === "9999" && descripcion.length === 0) {
        throw new Error("Describe la alerta cuando selecciones otra alerta.");
      }
      await configurarAlertaFormatoExportacion(token, x.id, prioridad, tipoAlerta, descripcion || null);
      await Promise.all([recargarExportaciones(), listarTemporizadores(token).then(setRelojes)]);
      const etiquetaFormato = x.formato_codigo === "ARRENDAMIENTO_XML" ? "ARI" : "INM";
      setAvisoOk(prioridad === PRIORIDAD_24H
        ? `Alerta ${etiquetaFormato} guardada. Se abrió el reloj de 24 horas y el XML deberá regenerarse.`
        : `Alerta ${etiquetaFormato} normalizada. El reloj de 24 horas quedó cancelado si existía.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar la alerta.");
    } finally {
      setOcupado(null);
    }
  };

  const descargarXmlExportacion = async (x: FormatoExportacionRow) => {
    const xml = await obtenerXmlExportacion(token, x.id);
    if (!xml) {
      setError("La exportación aún no tiene XML generado.");
      return;
    }
    const blob = new Blob([xml], { type: "application/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${x.formato_codigo.toLowerCase()}-${x.id.slice(0, ID_CORTO_LEN)}.xml`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const generarExportacion = async (x: FormatoExportacionRow, confirmarSinBc = false) => {
    setError(null);
    setAvisoOk(null);
    setOcupado(x.id);
    try {
      await generarXmlExportacion(token, x.id, x.formato_codigo, confirmarSinBc);
      setConfirmBc(null);
      await recargarExportaciones();
      setAvisoOk(
        x.formato_codigo === "ARRENDAMIENTO_XML"
          ? "XML de Arrendamiento generado. Revisa el archivo antes de validarlo y subirlo al SPPLD."
          : "XML de Inmuebles generado. Revisa el archivo antes de validarlo y subirlo al SPPLD.",
      );
    } catch (e) {
      // Compuerta BC (issue BC-3): el aviso saldría sin beneficiario controlador.
      // No es un error: es una confirmación explícita pendiente del REC.
      if (e instanceof ApiError && e.code === CODIGO_BC_AUSENTE) {
        setConfirmBc({ id: x.id, mensaje: e.message });
      } else {
        setError(e instanceof Error ? e.message : "No se pudo generar el XML.");
      }
    } finally {
      setOcupado(null);
    }
  };

  const validarExportacion = async (x: FormatoExportacionRow) => {
    setError(null);
    setAvisoOk(null);
    setOcupado(x.id);
    try {
      await validarFormatoExportacion(token, x.id);
      await recargarExportaciones();
      setAvisoOk("Paquete validado. El siguiente paso ocurre fuera de Argus: cargar el XML en el SPPLD con la e.firma del sujeto obligado.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo validar el paquete.");
    } finally {
      setOcupado(null);
    }
  };

  const presentarExportacion = async (x: FormatoExportacionRow) => {
    setError(null);
    setAvisoOk(null);
    setOcupado(x.id);
    try {
      const r = await presentarFormatoExportacion(token, x.id);
      await recargarExportaciones();
      setAvisoOk(r.advertencia_efirma ?? "Formato marcado como presentado. Captura el acuse cuando el portal lo entregue.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo presentar el formato.");
    } finally {
      setOcupado(null);
    }
  };

  const capturarAcuseExportacion = async (x: FormatoExportacionRow) => {
    setError(null);
    setAvisoOk(null);
    setOcupado(x.id);
    try {
      const folio = (foliosAcuse[x.id] ?? "").trim();
      const archivo = archivosAcuse[x.id];
      if (!FOLIO_ACUSE_RE.test(folio)) throw new Error("Captura un folio de acuse con formato AAAA-1..9 dígitos.");
      if (!archivo) throw new Error("Adjunta el archivo del acuse descargado del SPPLD.");
      const documentoId = await subirAcuseFormatoExportacion({ token, tenantId, exportacionId: x.id, archivo });
      await capturarAcuseFormatoExportacion(token, x.id, folio, documentoId);
      await Promise.all([recargarExportaciones(), listarTemporizadores(token).then(setRelojes)]);
      setArchivosAcuse((actual) => ({ ...actual, [x.id]: null }));
      setAvisoOk("Acuse capturado. El reloj de aviso quedó cerrado.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo capturar el acuse.");
    } finally {
      setOcupado(null);
    }
  };

  const cancelarExportacion = async (x: FormatoExportacionRow) => {
    setError(null);
    setAvisoOk(null);
    setOcupado(x.id);
    try {
      await cancelarFormatoExportacion(token, x.id, "Cancelado desde Pendientes por el REC.");
      await Promise.all([recargarExportaciones(), listarTemporizadores(token).then(setRelojes)]);
      setAvisoOk("Exportación cancelada. Si la operación sigue siendo avisable, debe corregirse desde la operación.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo cancelar la exportación.");
    } finally {
      setOcupado(null);
    }
  };

  const fila = (icono: ReactNode, titulo: string, sub: string, onClick?: () => void, tono?: "rojo" | "ambar") => (
    <button
      key={titulo + sub}
      onClick={onClick}
      disabled={!onClick}
      style={{
        width: "100%",
        textAlign: "left",
        display: "flex",
        gap: 12,
        alignItems: "center",
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderLeft: `4px solid ${tono === "rojo" ? C.red : tono === "ambar" ? C.amber : C.line}`,
        borderRadius: 12,
        padding: "11px 14px",
        marginBottom: 8,
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <span style={{ flexShrink: 0, display: "inline-flex", color: C.tielDark }}>{icono}</span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: "block", fontWeight: 700, fontSize: 13.5, color: C.ink }}>{titulo}</span>
        <span style={{ display: "block", fontSize: 12.5, color: C.muted }}>{sub}</span>
      </span>
      {onClick && <span style={{ marginLeft: "auto", color: C.tielDark, fontWeight: 700, flexShrink: 0 }}>›</span>}
    </button>
  );

  const nadaPendiente =
    !cargando && casosAbiertos.length === 0 && informesPendientes.length === 0 &&
    exportacionesPendientes.length === 0 && relojes.length === 0 && incompletas.length === 0;

  return (
    <main className="argus-detail-pane" style={{ flex: 1, overflow: "auto", padding: "22px 26px", minWidth: 0 }}>
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <h2 style={{ ...st.display, margin: "0 0 2px", fontSize: 20 }}>Pendientes</h2>
        <p style={{ margin: "0 0 16px", color: C.muted, fontSize: 13 }}>
          Lo que el sistema vigila y necesita de ti. Cuando esta lista esté vacía, estás al día.
        </p>

        {demo && (
          <div style={{ ...st.tarjeta, background: C.amberBg, borderColor: "#F0D9B5" }}>
            <span style={{ fontSize: 12.5, color: "#6d4a00" }}>
              Modo demostración — sin backend conectado esta vista no tiene señales reales que
              agregar. Conectado, aquí aparecen casos, informes, relojes y expedientes.
            </span>
          </div>
        )}
        {error && (
          <div style={{ ...st.tarjeta, background: C.redBg, borderColor: "#F2C4BE" }}>
            <span style={{ fontSize: 13, color: C.red }}>{error}</span>
          </div>
        )}
        {avisoOk && (
          <div style={{ ...st.tarjeta, background: C.greenBg, borderColor: "#BFE3CC" }}>
            <span style={{ fontSize: 13, color: C.green }}>{avisoOk}</span>
          </div>
        )}
        {cargando && <p style={{ color: C.muted, fontSize: 13 }}>Cargando…</p>}

        {casosAbiertos.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <p style={st.eyebrow}>Requieren tu decisión ({String(casosAbiertos.length)})</p>
            {casosAbiertos.map((c) =>
              fila(<IconoCumplimiento size={20} />, c.titulo, "Caso abierto — decide con fundamento; queda sellado.", () => { irA("cumplimiento"); }, "rojo"),
            )}
          </div>
        )}

        {informesPendientes.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <p style={st.eyebrow}>Informes por cerrar ({String(informesPendientes.length)})</p>
            {informesPendientes.map((i) =>
              fila(
                <IconoReporte size={20} />,
                `Informe ${i.mes_reportado} — ${estadoInformeTexto(i.estado)}`,
                i.estado === "presentado" ? "Presentado SIN acuse — el ciclo no está cumplido." : `Vence ${fecha(i.vence_en)}.`,
                () => { irA("reportes"); },
                i.estado === "presentado" ? "rojo" : "ambar",
              ),
            )}
          </div>
        )}

        {exportacionesPendientes.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <p style={st.eyebrow}>XML Inmuebles/Arrendamiento por cerrar ({String(exportacionesPendientes.length)})</p>
            {exportacionesPendientes.map((x) => {
              const enProceso = ocupado === x.id;
              // INM (fr. V) y ARI (fr. XV, OT-7): el ciclo de presentación/acuse vive
              // en el INFORME mensual, no en la exportación. La exportación es solo
              // borrador por aportación: se genera el XML (borrador) y el REC va al
              // informe del mes para presentar y capturar el acuse. Los botones de
              // presentar/validar/acuse a nivel exportación quedan para el flujo legacy
              // (hoy sin otro formato activo).
              const esInm = x.formato_codigo === "INMUEBLES_XML" || x.formato_codigo === "ARRENDAMIENTO_XML";
              const alertasCatalogo = x.formato_codigo === "ARRENDAMIENTO_XML" ? alertasAri : alertasInm;
              const puedeGenerar = x.estado === "borrador" || x.estado === "error";
              const puedeDescargar = x.estado === "generado" || x.estado === "validado" || x.estado === "presentado";
              const puedeValidar = !esInm && x.estado === "generado";
              const puedePresentar = !esInm && (x.estado === "generado" || x.estado === "validado");
              const puedeAcuse = !esInm && x.estado === "presentado";
              const puedeCancelar = x.estado === "borrador" || x.estado === "generado" || x.estado === "validado" || x.estado === "error";
              const archivoAcuse = archivosAcuse[x.id];
              const alertaDraft = alertaDraftDe(x);
              const prioridad24h = alertaDraft.prioridad === "2";
              const alertaInvalida =
                (alertaDraft.prioridad === "1" && alertaDraft.tipo_alerta !== ALERTA_SIN_ALERTA) ||
                (prioridad24h && alertaDraft.tipo_alerta === ALERTA_SIN_ALERTA) ||
                (alertaDraft.tipo_alerta === "9999" && alertaDraft.descripcion_alerta.trim().length === 0);
              return (
                <div
                  key={x.id}
                  style={{
                    ...st.tarjeta,
                    borderLeft: `4px solid ${x.estado === "error" ? C.red : puedeAcuse ? C.red : C.amber}`,
                    opacity: enProceso ? OPACIDAD_EN_PROCESO : 1,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13.5, color: C.ink }}>
                        {x.formato_codigo} — {x.estado.replace(/_/g, " ")}
                      </div>
                      <div style={{ fontSize: 12.5, color: C.muted, marginTop: 2 }}>
                        {esInm
                          ? (puedeGenerar
                              ? "Borrador de la aportación fr. V/XV — genera el XML y preséntalo desde el informe del mes."
                              : `Borrador listo — el informe mensual del mes reúne todos los avisos y cierra el ciclo.`)
                          : puedeAcuse
                            ? "Presentado SIN acuse — captura el folio para cerrar el reloj."
                            : puedeGenerar
                              ? "Pendiente de generar XML desde la operación fr. V."
                              : `Exportación ${x.id.slice(0, ID_CORTO_LEN)} · ${x.xml_sha256 ? `XML ${x.xml_sha256.slice(0, HASH_CORTO_LEN)}…` : "XML sin hash de presentación"}`}
                      </div>
                    </div>
                    {enProceso && <span style={{ color: C.tielDark, fontSize: 12, fontWeight: 700 }}>Procesando…</span>}
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                    {puedeGenerar && (
                      <button disabled={enProceso} style={botonAccion()} onClick={() => { void generarExportacion(x); }}>
                        {esInm ? "Generar XML (borrador)" : "Generar XML"}
                      </button>
                    )}
                    {esInm && (
                      <button
                        disabled={enProceso}
                        style={botonAccion()}
                        onClick={() => { irA("reportes"); }}
                      >
                        Ver informe del mes ›
                      </button>
                    )}
                    {puedeDescargar && (
                      <button
                        disabled={enProceso}
                        style={botonAccion()}
                        onClick={() => {
                          setError(null);
                          setAvisoOk(null);
                          setOcupado(x.id);
                          void descargarXmlExportacion(x).catch((e: unknown) => {
                            setError(e instanceof Error ? e.message : "No se pudo descargar el XML.");
                          }).finally(() => { setOcupado(null); });
                        }}
                      >
                        Descargar XML
                      </button>
                    )}
                    {puedeValidar && (
                      <button disabled={enProceso} style={botonAccion()} onClick={() => { void validarExportacion(x); }}>
                        Validar paquete
                      </button>
                    )}
                    {puedePresentar && (
                      <button disabled={enProceso} style={botonAccion("primario")} onClick={() => { void presentarExportacion(x); }}>
                        Marcar presentado
                      </button>
                    )}
                    {puedeCancelar && (
                      <button disabled={enProceso} style={botonAccion("peligro")} onClick={() => { void cancelarExportacion(x); }}>
                        Cancelar
                      </button>
                    )}
                  </div>
                  {confirmBc?.id === x.id && (
                    <div style={{ marginTop: 10, padding: "11px 13px", borderRadius: 10, background: C.amberBg, border: "1px solid #F0D9B5" }}>
                      <div style={{ fontSize: 12.5, color: "#6d4a00", fontWeight: 600, marginBottom: 8 }}>
                        {confirmBc.mensaje}
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          disabled={enProceso}
                          style={botonAccion("primario")}
                          onClick={() => { void generarExportacion(x, true); }}
                        >
                          Confirmo que no existe BC — generar aviso
                        </button>
                        <button disabled={enProceso} style={botonAccion()} onClick={() => { setConfirmBc(null); }}>
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(88px, 0.6fr) minmax(160px, 1.4fr)",
                    gap: 8,
                    marginTop: 10,
                    alignItems: "center",
                  }}>
                    <select
                      value={alertaDraft.prioridad}
                      disabled={enProceso || x.estado === "presentado"}
                      onChange={(e) => {
                        const prioridad = e.target.value;
                        setAlertaDrafts((actual) => ({
                          ...actual,
                          [x.id]: {
                            ...alertaDraft,
                            prioridad,
                            tipo_alerta: prioridad === "1" ? ALERTA_SIN_ALERTA : alertaDraft.tipo_alerta,
                            descripcion_alerta: prioridad === "1" ? "" : alertaDraft.descripcion_alerta,
                          },
                        }));
                      }}
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        padding: "8px 10px",
                        border: `1px solid ${C.line}`,
                        borderRadius: 10,
                        fontSize: 12.5,
                        background: "#fff",
                      }}
                    >
                      <option value="1">Normal</option>
                      <option value="2">24 horas</option>
                    </select>
                    <select
                      value={alertaDraft.tipo_alerta}
                      disabled={enProceso || x.estado === "presentado" || alertaDraft.prioridad === "1"}
                      onChange={(e) => {
                        const tipoAlerta = e.target.value;
                        setAlertaDrafts((actual) => ({
                          ...actual,
                          [x.id]: {
                            ...alertaDraft,
                            tipo_alerta: tipoAlerta,
                            descripcion_alerta: tipoAlerta === ALERTA_SIN_ALERTA ? "" : alertaDraft.descripcion_alerta,
                          },
                        }));
                      }}
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        padding: "8px 10px",
                        border: `1px solid ${C.line}`,
                        borderRadius: 10,
                        fontSize: 12.5,
                        background: "#fff",
                      }}
                    >
                      {alertasCatalogo.map(([clave, nombre]) => (
                        <option key={clave} value={clave}>{clave} — {nombre}</option>
                      ))}
                    </select>
                    {prioridad24h && (
                      <input
                        value={alertaDraft.descripcion_alerta}
                        disabled={enProceso || x.estado === "presentado"}
                        onChange={(e) => {
                          const valor = e.target.value;
                          setAlertaDrafts((actual) => ({
                            ...actual,
                            [x.id]: { ...alertaDraft, descripcion_alerta: valor },
                          }));
                        }}
                        placeholder="Descripción de la alerta"
                        style={{
                          gridColumn: "1 / -1",
                          width: "100%",
                          boxSizing: "border-box",
                          padding: "8px 10px",
                          border: `1px solid ${C.line}`,
                          borderRadius: 10,
                          fontSize: 12.5,
                        }}
                      />
                    )}
                    <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontSize: 12, color: prioridad24h ? C.amber : C.muted }}>
                        {prioridad24h ? "Prioridad 24 horas: se abre reloj independiente." : "Prioridad normal: tipo 100 sin alerta."}
                      </span>
                      <button
                        disabled={enProceso || x.estado === "presentado" || alertaInvalida}
                        style={botonAccion()}
                        onClick={() => { void guardarAlertaExportacion(x); }}
                      >
                        Guardar alerta
                      </button>
                    </div>
                  </div>
                  {puedeAcuse && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
                      <input
                        value={foliosAcuse[x.id] ?? ""}
                        onChange={(e) => {
                          const valor = e.target.value;
                          setFoliosAcuse((actual) => ({ ...actual, [x.id]: valor }));
                        }}
                        placeholder="2026-123"
                        style={{
                          width: "100%",
                          boxSizing: "border-box",
                          padding: "9px 12px",
                          border: `1px solid ${C.line}`,
                          borderRadius: 10,
                          fontSize: 13,
                        }}
                      />
                      <input
                        type="file"
                        accept=".pdf,.xml,image/*"
                        onChange={(e) => {
                          const archivo = e.target.files?.[0] ?? null;
                          setArchivosAcuse((actual) => ({ ...actual, [x.id]: archivo }));
                        }}
                        style={{
                          width: "100%",
                          boxSizing: "border-box",
                          padding: "8px 10px",
                          border: `1px solid ${C.line}`,
                          borderRadius: 10,
                          fontSize: 12.5,
                          background: "#fff",
                        }}
                      />
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12, color: C.muted }}>
                          {archivoAcuse ? `Acuse: ${archivoAcuse.name}` : "Adjunta el acuse descargado del SPPLD."}
                        </span>
                        <button
                          disabled={enProceso || !FOLIO_ACUSE_RE.test((foliosAcuse[x.id] ?? "").trim()) || !archivoAcuse}
                          style={botonAccion("primario")}
                          onClick={() => { void capturarAcuseExportacion(x); }}
                        >
                          Guardar acuse
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {relojes.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <p style={st.eyebrow}>Relojes de ley en curso ({String(relojes.length)})</p>
            {relojes.map((r) =>
              fila(
                <IconoReloj size={20} />,
                tituloReloj(r),
                `Vence ${fecha(r.vence_en)} · estado: ${r.estado}${r.vence_en.slice(0, "YYYY-MM-DD".length) < hoy ? " · ¡VENCIDO!" : ""}`,
                undefined,
                r.estado === "vencido" ? "rojo" : r.estado === "escalado" ? "ambar" : undefined,
              ),
            )}
          </div>
        )}

        {incompletas.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <p style={st.eyebrow}>Expedientes incompletos ({String(incompletas.length)})</p>
            {incompletas.map((p) =>
              fila(<IconoPersonas size={20} />, nombrePersona(p), "Faltan requisitos del expediente — las operaciones no se liberan.", () => { irA("personas"); }, "ambar"),
            )}
          </div>
        )}

        {nadaPendiente && !demo && (
          <div style={{ ...st.tarjeta, background: C.greenBg, borderColor: "#BFE3CC" }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.green }}>
              ✓ Todo al día — sin casos abiertos, sin informes pendientes, sin relojes en riesgo.
            </span>
          </div>
        )}
      </div>
    </main>
  );
}
